/**
 * Inline expanding header search with predictive results.
 *
 * Collapsed: a circular icon button. Open: an inline input (overlaid on
 * desktop, full-width below the header on mobile) with a predictive
 * dropdown fetched from /search/suggest.json.
 */
class CCSearch extends HTMLElement {
  #abortController = null;
  #debounceTimer = null;

  connectedCallback() {
    this.toggleButton = this.querySelector('[data-cc-search-toggle]');
    this.panel = this.querySelector('[data-cc-search-panel]');
    this.input = this.querySelector('[data-cc-search-input]');
    this.clearButton = this.querySelector('[data-cc-search-clear]');
    this.results = this.querySelector('[data-cc-search-results]');

    this.toggleButton?.addEventListener('click', this.#toggle);
    this.clearButton?.addEventListener('click', this.#clear);
    this.input?.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    this.#abortController?.abort();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  #toggle = () => {
    if (this.isOpen) {
      this.#close();
    } else {
      this.setAttribute('open', '');
      this.toggleButton?.setAttribute('aria-expanded', 'true');
      this.input?.focus();
    }
  };

  #close = () => {
    this.removeAttribute('open');
    this.toggleButton?.setAttribute('aria-expanded', 'false');
    this.#hideResults();
  };

  #clear = () => {
    if (this.input && this.input.value !== '') {
      this.input.value = '';
      this.input.focus();
      this.#hideResults();
      return;
    }
    this.#close();
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.stopPropagation();
      this.#close();
      this.toggleButton?.focus();
    }
  };

  #onDocumentClick = (event) => {
    if (this.isOpen && event.target instanceof Node && !this.contains(event.target)) {
      this.#close();
    }
  };

  #onInput = () => {
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    const query = this.input?.value.trim() ?? '';

    if (query.length < 2) {
      this.#hideResults();
      return;
    }

    this.#debounceTimer = setTimeout(() => this.#fetchSuggestions(query), 250);
  };

  async #fetchSuggestions(query) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '5',
      'resources[options][unavailable_products]': 'hide',
      'resources[options][fields]': 'title,product_type,variants.title',
    });

    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}search/suggest.json?${params}`, {
        signal: this.#abortController.signal,
      });
      if (!response.ok) return;
      const data = await response.json();
      const products = data?.resources?.results?.products ?? [];
      this.#renderResults(query, products);
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  }

  /**
   * @param {string} query
   * @param {Array<{title: string, url: string, image?: string, featured_image?: {url: string}}>} products
   */
  #renderResults(query, products) {
    if (!this.results) return;
    this.results.textContent = '';

    for (const product of products) {
      const item = document.createElement('a');
      item.className = 'cc-search__result';
      item.setAttribute('role', 'option');
      item.href = product.url;

      const imageUrl = product.image || product.featured_image?.url;
      if (imageUrl) {
        const img = document.createElement('img');
        img.className = 'cc-search__result-image';
        img.src = this.#sizedImage(imageUrl, 64);
        img.alt = '';
        img.width = 32;
        img.height = 32;
        img.loading = 'lazy';
        item.appendChild(img);
      }

      item.appendChild(this.#highlight(this.#displayTitle(product.title), query));
      this.results.appendChild(item);
    }

    const viewMore = document.createElement('a');
    viewMore.className = 'cc-search__view-more';
    viewMore.href = `${window.Shopify?.routes?.root ?? '/'}search?q=${encodeURIComponent(query)}`;
    viewMore.textContent = this.dataset.viewMoreLabel || 'View more products';
    this.results.appendChild(viewMore);

    this.results.hidden = false;
    this.input?.setAttribute('aria-expanded', 'true');
  }

  #hideResults() {
    if (!this.results) return;
    this.results.hidden = true;
    this.results.textContent = '';
    this.input?.setAttribute('aria-expanded', 'false');
  }

  /** Strip the ", Bag of N" suffix used across the catalog */
  #displayTitle(title) {
    const index = title.indexOf(', Bag of ');
    return index === -1 ? title : title.slice(0, index);
  }

  /** Wrap query matches in <mark> */
  #highlight(title, query) {
    const span = document.createElement('span');
    span.className = 'cc-search__result-title';
    const index = title.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) {
      span.textContent = title;
      return span;
    }
    span.append(
      document.createTextNode(title.slice(0, index)),
      Object.assign(document.createElement('mark'), { textContent: title.slice(index, index + query.length) }),
      document.createTextNode(title.slice(index + query.length))
    );
    return span;
  }

  /** Request an appropriately sized Shopify CDN image */
  #sizedImage(url, size) {
    return url.replace(/(\.[a-z]{3,4})(\?|$)/, `_${size}x${size}$1$2`);
  }
}

if (!customElements.get('cc-search')) {
  customElements.define('cc-search', CCSearch);
}
